import { OrderAPI } from '@business/api/order';
import {
  CompactOrderResponse,
  OrderId,
  OrderProductId,
  OrderRequest,
  OrderResponse,
  OrderShipmentId,
  OrderShipmentRequest,
  OrderShipmentStatus,
  OrderVendorId,
} from '@business/entities/order';
import { OrgId } from '@business/entities/organization';
import { InvoiceId } from '@business/entities/TransactionInvoice';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { gcLabel } from '@core/utils/hooks/misc/gcLabel';
import { Repository } from '@core/utils/repository';
import { showConfirmation, showDeleteConfirmation } from '@modules/common';
import { switchMap } from 'rxjs/operators';
import {
  OrderCancelRequest,
  OrderEmailTemplateRequest,
  OrderEstimateId,
  OrderShippingRequest,
  OrderStatus,
} from './../../entities/order/OrderResponse';
import { OrderStageId } from './../../entities/order/OrderStageResponse';
import { PoType, PurchaseOrderId } from './../../entities/order/PurchaseOrderResponse';

export class OrderBloc extends BaseBloc {
  private readonly itemRepo = new Repository<CompactOrderResponse | OrderResponse>({
    getItemId: item => item.id,
  });

  readonly orders$ = this.itemRepo.items$;
  readonly selectOrder = this.itemRepo.selectItem;

  onReset() {
    this.itemRepo.reset();
  }

  fetchOrders = (orgId?: OrgId, status?: OrderStatus) => {
    return OrderAPI.fetchOrders(orgId, status).pipe(this.itemRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_order_error') }));
  };

  fetchOrgOrders = (orgId: OrgId) => {
    return OrderAPI.fetchOrgOrders(orgId).pipe(this.itemRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_order_error') }));
  };

  searchOrders = (search: string) => {
    return OrderAPI.searchOrders(search).pipe(handleMessage({ error: requestMessage('fetch_order_error') }));
  };

  fetchOrder = (id: OrderProductId, fromNgrok = false) => {
    return OrderAPI.fetchOrder(id, fromNgrok).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_order_error') }),
    );
  };

  createOrder = (data: OrderRequest) => {
    return OrderAPI.createOrder(data).pipe(
      this.itemRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_order'),
      }),
    );
  };

  updateEstimate = (id: OrderId, data: OrderRequest) => {
    return OrderAPI.updateEstimate(id, data).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order'),
      }),
    );
  };

  updateInvoice = (id: InvoiceId, data: OrderRequest) => {
    return OrderAPI.updateInvoice(id, data).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order'),
      }),
    );
  };

  createInvoice = (id: OrderId, data: OrderRequest) => {
    return OrderAPI.createInvoice(id, data).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order'),
      }),
    );
  };

  updateOrder = (id: OrderId, data: Partial<OrderRequest>, isSend = false) => {
    return OrderAPI.updateOrder(id, data, isSend).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order'),
      }),
    );
  };

  deleteOrder = (id: OrderProductId) => {
    return showDeleteConfirmation('Delete order', `Do you really want to remove this order?`).pipe(
      switchMap(() => OrderAPI.deleteOrder(id)),
      this.itemRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_order'),
      }),
    );
  };

  updateStage = (
    id: OrderId,
    stageId: OrderStageId,
    deliveryDate?: number,
    closeIt = false,
    sendIt?: boolean,
    emailData?: OrderEmailTemplateRequest,
  ) => {
    return OrderAPI.updateStage(id, stageId, deliveryDate, closeIt, sendIt, emailData).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order_stage'),
      }),
    );
  };

  updateStatus = (id: OrderId, status: OrderStatus, reason?: string, isSend?: boolean, data?: OrderCancelRequest) => {
    return OrderAPI.updateStatus(id, status, reason, isSend, data).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order_status'),
      }),
    );
  };

  updateShipping = (id: OrderId, stageId: OrderStageId, data: OrderShippingRequest) => {
    return OrderAPI.updateShipping(id, stageId, data).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order_stage'),
      }),
    );
  };

  acceptEstimate = (id: OrderEstimateId, name: string, email: string) => {
    return showConfirmation(`Accept ${gcLabel('Estimate')}`, `Do you really want to accept this ${gcLabel('estimate')}?`).pipe(
      switchMap(() => OrderAPI.acceptEstimate(id, name, email)),
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('accept_order'),
      }),
    );
  };

  createOrderInvoice = (id: OrderEstimateId, invoiceId: InvoiceId, sent: boolean, data?: Partial<OrderRequest>) => {
    return OrderAPI.createOrderInvoice(id, invoiceId, sent, data).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order_status'),
      }),
    );
  };

  sendEstimate = (id: OrderEstimateId, data?: Partial<OrderRequest>, withoutAsking = false) => {
    if (withoutAsking) {
      return OrderAPI.sendEstimate(id, data).pipe(
        this.itemRepo.ops.upsertOne(item => ({ item })),
        handleMessage({
          type: requestMessage('accept_order'),
        }),
      );
    }
    return showConfirmation(`Send ${gcLabel('Estimate')}`, `Do you really want to send this ${gcLabel('estimate')} to customer?`).pipe(
      switchMap(() => OrderAPI.sendEstimate(id, data)),
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('accept_order'),
      }),
    );
  };

  rejectEstimate = (id: OrderEstimateId, reason: string) => {
    return OrderAPI.rejectEstimate(id, reason).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('reject_order'),
      }),
    );
  };

  sendPurchaseOrder = (id: OrderEstimateId, vendorId: OrderVendorId, type: PoType, data: Partial<OrderEmailTemplateRequest>) => {
    return OrderAPI.sendPurchaseOrder(id, vendorId, type, data).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('send_purchase_order'),
      }),
    );
  };

  createPurchaseOrder = (id: OrderId, vendorId?: OrderVendorId) => {
    return OrderAPI.createPurchaseOrder(id, vendorId).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order'),
      }),
    );
  };

  closePurchaseOrder = (poId: PurchaseOrderId) => {
    return showDeleteConfirmation('Cancel Purchase Order', `Do you really want to cancel this purchase order?`, {
      btnLabel: 'Confirm',
    }).pipe(
      switchMap(() => OrderAPI.closePurchaseOrder(poId)),
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order_product'),
      }),
    );
  };

  purchaseOrderStatus = (poId: PurchaseOrderId, status: OrderShipmentStatus, data: Partial<OrderShipmentRequest>) => {
    return OrderAPI.purchaseOrderStatus(poId, status, data).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('status_update'),
      }),
    );
  };

  updatePurchaseOrder = (id: PurchaseOrderId, data: Partial<OrderRequest>) => {
    return OrderAPI.updatePurchaseOrder(id, data).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order'),
      }),
    );
  };
  saveBillOfLading = (id: PurchaseOrderId, data: Partial<OrderRequest>) => {
    return OrderAPI.saveBillOfLading(id, data).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order'),
      }),
    );
  };

  savePickList = (id: PurchaseOrderId, data: Partial<OrderRequest>) => {
    return OrderAPI.savePickList(id, data).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order'),
      }),
    );
  };

  download = (orderId: OrderId, vendorId: OrderVendorId, filename: string, type: PoType) => {
    return OrderAPI.download(orderId, vendorId, filename, type).pipe(handleMessage({ error: requestMessage('print_file_error') }));
  };

  downloadPdf = (purchaseOrderId: PurchaseOrderId, filename: string, type: PoType) => {
    return OrderAPI.downloadPdf(purchaseOrderId, filename, type).pipe(handleMessage({ error: requestMessage('print_file_error') }));
  };

  downloadLading = (id: PurchaseOrderId, filename: string) => {
    return OrderAPI.downloadLading(id, filename).pipe(handleMessage({ error: requestMessage('print_file_error') }));
  };

  downloadPickList = (id: PurchaseOrderId, filename: string) => {
    return OrderAPI.downloadPickList(id, filename).pipe(handleMessage({ error: requestMessage('print_file_error') }));
  };

  createShipment = (poId: PurchaseOrderId, status: OrderShipmentStatus, data: Partial<OrderShipmentRequest>, isSend?: boolean) => {
    return OrderAPI.createShipment(poId, status, data, isSend).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('status_update'),
      }),
    );
  };

  updateShipment = (id: OrderShipmentId, data: OrderShippingRequest, isSend?: boolean) => {
    return OrderAPI.updateShipment(id, data, isSend).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_shipment'),
      }),
    );
  };

  deleteShipment = (id: OrderProductId) => {
    return showDeleteConfirmation('Delete Shipment', `Do you really want to remove this shipment?`).pipe(
      switchMap(() => OrderAPI.deleteShipment(id)),
      this.itemRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_shipment'),
      }),
    );
  };

  deleteInvoice = (id: InvoiceId, orderId: OrderId) => {
    return showDeleteConfirmation('Delete Invoice', `Do you really want to remove this invoice?`).pipe(
      switchMap(() => OrderAPI.deleteInvoice(id, orderId)),
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('delete_invoice'),
      }),
    );
  };
}
